import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Public sandbox credentials — intentionally shipped in the bundle; they are
// the same creds published in outreach messages. The account is 2FA-bypassed
// via SANDBOX_ACCOUNTS and holds dummy data only.
const DEMO_EMAIL = "demo@elementar.app";
const DEMO_PASSWORD = "123456789"; // pragma: allowlist secret

/**
 * One-click demo entry for outreach: /demo logs straight into the demo
 * tenant and lands on Home. source:"demo" relabels the
 * login event (method=demo_autologin) for campaign attribution.
 *
 * Per-arm attribution: /demo?s=<tag> (e.g. /demo?s=il-w2) rides along as
 * source:"demo:<tag>" and lands in the login event's `campaign` dimension, so
 * each outreach wave's click→entry rate is countable instead of inferred from
 * send timestamps. Untagged /demo still works and reports campaign="".
 * The backend sanitizes the tag; this only bounds its length.
 */
export default function DemoLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const tag = searchParams.get("s") || "";
      const source = tag ? `demo:${tag.slice(0, 32)}` : "demo";
      try {
        // A returning visitor can arrive holding an expired token: login succeeds,
        // the stale value survives, and the auth guard bounces them to /login with
        // no password. Outreach DMs sit in inboxes for days, so re-clicks ARE the
        // common case. Clear first; /demo is itself a login, so dropping any
        // existing session is the intended behaviour, not a side effect.
        localStorage.removeItem("token");
        localStorage.removeItem("csrf_token");
        await login(DEMO_EMAIL, DEMO_PASSWORD, source);
        sessionStorage.setItem("elementar_auto_tour", "1");
        // Land on Home: the tour opens there.
        navigate("/home", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
    // started.current makes this effectively run-once; deps are listed only to
    // satisfy exhaustive-deps.
  }, [login, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-pulse text-lg font-medium text-gray-700">
          Opening the Elementar demo…
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Logging you into the sandbox, one moment.
        </div>
      </div>
    </div>
  );
}
